// src/components/SocialMediaView.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import { Users, Search, Edit, Save, X, ExternalLink, GripVertical, CheckCircle, XCircle, Eye } from 'lucide-react';

const SocialMediaView = ({ canSeeSuperAdminSettings, canEditIncidents, setShowAddSocialMedia }) => {
  const { getToken, isSignedIn } = useAuth();
  const { activeCompany } = useTenant();

  // State
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [editedAccount, setEditedAccount] = useState(null);
  
  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(420);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);

  // Platform configurations
  const PLATFORMS = [
    { name: 'Facebook', icon: 'ðŸ“˜', color: '#1877F2' },
    { name: 'Instagram', icon: 'ðŸ“¸', color: '#E4405F' },
    { name: 'Twitter/X', icon: 'ðŸ¦', color: '#1DA1F2' },
    { name: 'LinkedIn', icon: 'ðŸ’¼', color: '#0A66C2' },
    { name: 'TikTok', icon: 'ðŸŽµ', color: '#000000' },
    { name: 'YouTube', icon: 'â–¶ï¸', color: '#FF0000' },
    { name: 'Telegram', icon: 'âœˆï¸', color: '#0088CC' },
    { name: 'Discord', icon: 'ðŸ’¬', color: '#5865F2' }
  ];

  const ACCOUNT_TYPES = [
    'Impersonation',
    'Brand Abuse',
    'Phishing',
    'Scam',
    'Fake Customer Support',
    'Fake Executive',
    'Fake Giveaway',
    'Malware Distribution',
    'Credential Harvesting'
  ];

  const STATUS_OPTIONS = [
    'Active',
    'Monitoring',
    'Takedown Requested',
    'Takedown Successful',
    'Resolved',
    'False Positive'
  ];

  const RISK_LEVELS = [
    'Critical',
    'High',
    'Medium',
    'Low',
    'Informational'
  ];

  // Get platform config
  const getPlatformConfig = (platformName) => {
    return PLATFORMS.find(p => p.name === platformName) || { name: platformName, icon: 'ðŸŒ', color: '#6B7280' };
  };

  // Fetch social media accounts
  useEffect(() => {
    if (!isSignedIn || !activeCompany) return;
    
    const fetchAccounts = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await fetch(`/api/companies/${activeCompany.id}/social-media`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!res.ok) throw new Error(`Failed to load social media accounts (${res.status})`);
        
        const data = await res.json();
        setAccounts(Array.isArray(data) ? data : []);
        
        if (data.length > 0 && !selectedAccount) {
          setSelectedAccount(data[0]);
        }
      } catch (e) {
        console.error('Social media accounts load error:', e);
        setError(e.message || 'Failed to load social media accounts');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAccounts();
  }, [isSignedIn, activeCompany, getToken]);

  // Handle mouse down on resizer
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      
      const minWidth = 280;
      const maxWidth = containerRect.width * 0.7;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Filter accounts
  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = account.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         account.handle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         account.account_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlatform = filterPlatform === 'all' || account.platform === filterPlatform;
    const matchesStatus = filterStatus === 'all' || account.status === filterStatus;
    const matchesRisk = filterRisk === 'all' || account.risk === filterRisk;
    return matchesSearch && matchesPlatform && matchesStatus && matchesRisk;
  });

  // Get risk color
  const getRiskColor = (risk) => {
    const colors = {
      'Critical': 'bg-red-500/20 text-red-400 border-red-500/30',
      'High': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Medium': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Low': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Informational': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[risk] || colors['Informational'];
  };

  // Get status color
  const getStatusColor = (status) => {
    if (status === 'Active') return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (status?.includes('Takedown')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    if (status?.includes('Resolved')) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status === 'Monitoring') return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    if (status === 'False Positive') return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // Format number with K/M suffix
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Handle edit
  const handleEdit = () => {
    setEditedAccount({ ...selectedAccount });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedAccount(null);
    setIsEditing(false);
  };

  // Save changes
  const handleSave = async () => {
    if (!editedAccount || !activeCompany) {
      alert('No account selected or company context missing.');
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(
        `/api/companies/${activeCompany.id}/social-media/${editedAccount.account_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            platform: editedAccount.platform,
            account_name: editedAccount.account_name,
            handle: editedAccount.handle,
            profile_url: editedAccount.profile_url,
            type: editedAccount.type,
            status: editedAccount.status,
            risk: editedAccount.risk,
            followers: editedAccount.followers,
            following: editedAccount.following,
            posts_count: editedAccount.posts_count,
            verified: editedAccount.verified,
            discovered_date: editedAccount.discovered_date,
            account_created_date: editedAccount.account_created_date,
            content_type: editedAccount.content_type,
            impersonated_entity: editedAccount.impersonated_entity,
            screenshot_url: editedAccount.screenshot_url,
            notes: editedAccount.notes
          })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Failed to update account (HTTP ${response.status})`);
      }

      const updated = await response.json();

      // Update local state
      setAccounts(accounts.map(a => 
        a.account_id === updated.account_id ? updated : a
      ));
      setSelectedAccount(updated);
      setIsEditing(false);
      setEditedAccount(null);
      alert(`Account "${updated.account_id}" updated successfully!`);
    } catch (error) {
      console.error('Error saving account:', error);
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <div ref={containerRef} className="h-full flex relative">
      {/* LEFT SIDEBAR - Account List */}
      <div 
        className="border-r border-slate-800/50 bg-slate-900/30 flex flex-col"
        style={{ width: `${leftPanelWidth}px`, minWidth: '280px', maxWidth: '70%' }}
      >
        <div className="px-4 py-4 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Social Media Monitoring
            </h2>
            {canSeeSuperAdminSettings && (
              <button
                onClick={() => setShowAddSocialMedia(true)}
                className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-xs text-white font-medium transition-all"
              >
                + New
              </button>
            )}
          </div>

          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Platforms</option>
              {PLATFORMS.map(platform => (
                <option key={platform.name} value={platform.name}>
                  {platform.icon} {platform.name}
                </option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Status</option>
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <select
              value={filterRisk}
              onChange={(e) => setFilterRisk(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Risk Levels</option>
              {RISK_LEVELS.map(risk => (
                <option key={risk} value={risk}>{risk}</option>
              ))}
            </select>
          </div>

          <div className="mt-3 text-xs text-slate-400">
            {filteredAccounts.length} account{filteredAccounts.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!activeCompany && (
            <div className="p-4 text-sm text-slate-400">No company selected.</div>
          )}
          {error && (
            <div className="m-4 bg-red-500/10 border border-red-500/30 rounded p-3 text-sm text-red-300">
              {error}
            </div>
          )}
          {loading && (
            <div className="p-4 text-sm text-slate-400">Loading accounts...</div>
          )}
          {!loading && filteredAccounts.length === 0 && (
            <div className="p-4 text-sm text-slate-500">No accounts found.</div>
          )}
          {!loading && filteredAccounts.map((account) => {
            const isSelected = selectedAccount?.account_id === account.account_id;
            const platformConfig = getPlatformConfig(account.platform);
            
            return (
              <button
                key={account.account_id}
                onClick={() => {
                  setSelectedAccount(account);
                  setIsEditing(false);
                  setEditedAccount(null);
                }}
                className={`w-full p-4 border-b border-slate-800/30 text-left transition-all ${
                  isSelected
                    ? 'bg-cyan-500/10 border-l-4 border-l-cyan-500'
                    : 'hover:bg-slate-800/30 border-l-4 border-l-transparent'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{platformConfig.icon}</span>
                    <span className="text-xs text-slate-400">{account.platform}</span>
                    {account.verified && (
                      <CheckCircle className="w-3 h-3 text-blue-400" title="Verified Account" />
                    )}
                  </div>
                  <span className="text-xs text-slate-500">{account.discovered_date || '-'}</span>
                </div>

                <div className="text-sm font-medium text-slate-200 mb-1">
                  {account.account_name}
                </div>

                <div className="text-xs text-slate-400 mb-2 font-mono">
                  {account.handle || '-'} â€¢ {formatNumber(account.followers)} followers
                </div>

                <div className="text-xs text-slate-500 mb-2">
                  {account.account_id}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(account.status)}`}>
                    {account.status}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getRiskColor(account.risk)}`}>
                    {account.risk}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* RESIZABLE SEPARATOR */}
      <div
        className={`w-1 bg-slate-800/50 hover:bg-cyan-500/50 cursor-col-resize flex items-center justify-center transition-colors relative group ${
          isResizing ? 'bg-cyan-500/70' : ''
        }`}
        onMouseDown={handleMouseDown}
      >
        <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center">
          <GripVertical className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors" />
        </div>
      </div>

      {/* RIGHT PANEL - Account Details */}
      <div className="flex-1 overflow-y-auto">
        {selectedAccount ? (
          <div className="p-8">
            <div className="mb-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{getPlatformConfig(selectedAccount.platform).icon}</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedAccount.account_name}
                        onChange={(e) => setEditedAccount({ ...editedAccount, account_name: e.target.value })}
                        className="text-2xl font-bold bg-slate-800/50 border border-slate-700/50 rounded px-3 py-1 text-slate-100 flex-1"
                      />
                    ) : (
                      <h2 className="text-2xl font-bold text-slate-100">
                        {selectedAccount.account_name}
                      </h2>
                    )}
                    {selectedAccount.verified && !isEditing && (
                      <CheckCircle className="w-6 h-6 text-blue-400" title="Verified Account" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-400">
                    <span className="font-mono">{selectedAccount.account_id}</span>
                    <span>|</span>
                    {isEditing ? (
                      <select
                        value={editedAccount.platform}
                        onChange={(e) => setEditedAccount({ ...editedAccount, platform: e.target.value })}
                        className="bg-slate-800/50 border border-slate-700/50 rounded px-2 py-1 text-slate-200 text-sm"
                      >
                        {PLATFORMS.map(platform => (
                          <option key={platform.name} value={platform.name}>
                            {platform.icon} {platform.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{selectedAccount.platform}</span>
                    )}
                    <span>|</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedAccount.handle || ''}
                        onChange={(e) => setEditedAccount({ ...editedAccount, handle: e.target.value })}
                        className="bg-slate-800/50 border border-slate-700/50 rounded px-2 py-1 text-slate-200 text-sm font-mono"
                        placeholder="@username"
                      />
                    ) : (
                      <span className="font-mono">{selectedAccount.handle || '-'}</span>
                    )}
                  </div>
                </div>
                {!isEditing && (
                  <button
                    onClick={() => setSelectedAccount(null)}
                    className="p-2 hover:bg-slate-800/50 rounded-lg transition-all"
                  >
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                )}
              </div>

              {/* Status Badges */}
              <div className="flex items-center gap-2 mb-4">
                {isEditing ? (
                  <>
                    <select
                      value={editedAccount.status}
                      onChange={(e) => setEditedAccount({ ...editedAccount, status: e.target.value })}
                      className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                    <select
                      value={editedAccount.risk}
                      onChange={(e) => setEditedAccount({ ...editedAccount, risk: e.target.value })}
                      className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                    >
                      {RISK_LEVELS.map(risk => (
                        <option key={risk} value={risk}>{risk}</option>
                      ))}
                    </select>
                    <select
                      value={editedAccount.type}
                      onChange={(e) => setEditedAccount({ ...editedAccount, type: e.target.value })}
                      className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                    >
                      {ACCOUNT_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(selectedAccount.status)}`}>
                      {selectedAccount.status}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getRiskColor(selectedAccount.risk)}`}>
                      {selectedAccount.risk} Risk
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border border-purple-500/30 bg-purple-500/10 text-purple-400">
                      {selectedAccount.type}
                    </span>
                  </>
                )}
              </div>

              {/* Profile URL */}
              {(selectedAccount.profile_url || isEditing) && (
                <div className="mb-4">
                  {isEditing ? (
                    <input
                      type="url"
                      value={editedAccount.profile_url || ''}
                      onChange={(e) => setEditedAccount({ ...editedAccount, profile_url: e.target.value })}
                      className="w-full bg-slate-800/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200 text-sm"
                      placeholder="https://..."
                    />
                  ) : (
                    <a
                      href={selectedAccount.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Profile
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-6">
              {/* Metrics */}
              <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Account Metrics
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Followers</div>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editedAccount.followers || ''}
                        onChange={(e) => setEditedAccount({ ...editedAccount, followers: parseInt(e.target.value) || 0 })}
                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-2 py-1 text-slate-200 text-sm"
                      />
                    ) : (
                      <div className="text-lg font-bold text-slate-200">{formatNumber(selectedAccount.followers)}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Following</div>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editedAccount.following || ''}
                        onChange={(e) => setEditedAccount({ ...editedAccount, following: parseInt(e.target.value) || 0 })}
                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-2 py-1 text-slate-200 text-sm"
                      />
                    ) : (
                      <div className="text-lg font-bold text-slate-200">{formatNumber(selectedAccount.following)}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Posts</div>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editedAccount.posts_count || ''}
                        onChange={(e) => setEditedAccount({ ...editedAccount, posts_count: parseInt(e.target.value) || 0 })}
                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-2 py-1 text-slate-200 text-sm"
                      />
                    ) : (
                      <div className="text-lg font-bold text-slate-200">{formatNumber(selectedAccount.posts_count)}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Verified</div>
                    {isEditing ? (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editedAccount.verified || false}
                          onChange={(e) => setEditedAccount({ ...editedAccount, verified: e.target.checked })}
                          className="w-4 h-4 rounded bg-slate-800 border-slate-600"
                        />
                        <span className="text-sm text-slate-300">Verified</span>
                      </label>
                    ) : (
                      <div className="flex items-center gap-1">
                        {selectedAccount.verified ? (
                          <CheckCircle className="w-5 h-5 text-blue-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-slate-600" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Discovered Date
                  </h3>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editedAccount.discovered_date || ''}
                      onChange={(e) => setEditedAccount({ ...editedAccount, discovered_date: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedAccount.discovered_date || '-'}</p>
                  )}
                </div>
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Account Created
                  </h3>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editedAccount.account_created_date || ''}
                      onChange={(e) => setEditedAccount({ ...editedAccount, account_created_date: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedAccount.account_created_date || '-'}</p>
                  )}
                </div>
              </div>

              {/* Content Details */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Content Type
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedAccount.content_type || ''}
                      onChange={(e) => setEditedAccount({ ...editedAccount, content_type: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="e.g., Phishing Links, Fake Giveaways"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedAccount.content_type || '-'}</p>
                  )}
                </div>
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Impersonated Entity
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedAccount.impersonated_entity || ''}
                      onChange={(e) => setEditedAccount({ ...editedAccount, impersonated_entity: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="e.g., CEO, Support Team, Company"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedAccount.impersonated_entity || '-'}</p>
                  )}
                </div>
              </div>

              {/* Screenshot */}
              {(selectedAccount.screenshot_url || isEditing) && (
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Screenshot URL
                  </h3>
                  {isEditing ? (
                    <input
                      type="url"
                      value={editedAccount.screenshot_url || ''}
                      onChange={(e) => setEditedAccount({ ...editedAccount, screenshot_url: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="https://..."
                    />
                  ) : (
                    selectedAccount.screenshot_url && (
                      <a
                        href={selectedAccount.screenshot_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View Screenshot
                      </a>
                    )
                  )}
                </div>
              )}

              {/* Notes */}
              {(selectedAccount.notes || isEditing) && (
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                    Notes
                  </h3>
                  {isEditing ? (
                    <textarea
                      rows={4}
                      value={editedAccount.notes || ''}
                      onChange={(e) => setEditedAccount({ ...editedAccount, notes: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="Additional details about this account..."
                    />
                  ) : (
                    <p className="text-slate-200 whitespace-pre-wrap">{selectedAccount.notes}</p>
                  )}
                </div>
              )}

              {/* Metadata */}
              <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Metadata
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Created:</span>
                    <span className="ml-2 text-slate-200">
                      {selectedAccount.created_at ? new Date(selectedAccount.created_at).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Created By:</span>
                    <span className="ml-2 text-slate-200">{selectedAccount.created_by || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Updated:</span>
                    <span className="ml-2 text-slate-200">
                      {selectedAccount.updated_at ? new Date(selectedAccount.updated_at).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Updated By:</span>
                    <span className="ml-2 text-slate-200">{selectedAccount.updated_by || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {canEditIncidents && (
                <div className="flex gap-3">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-sm text-white transition-all flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleEdit}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Account
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Users className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-400">Select an account to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialMediaView;